// SCR-STAFF-201 تب «فرم‌ها و محدودیت‌ها» (AP-CAT-02): فرم‌ها و رضایت‌نامه‌های لازم با مرحله، الزامی بودن و مدت اعتبار رضایت
// (US-CAT-013؛ قالب‌های نمونه با نشان «نمونه») و محدودیت‌های سن، جنس، پیش‌نیاز، فاصله و تعداد در روز (US-CAT-005؛ هشدار — BR-CAT-006).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type FormTemplateOptionView,
  type RequiredFormDto,
  type RestrictionDto,
  type ServiceView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ErrorState,
  LoadingState,
  SampleBadge,
  SelectRHF,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch, type Control } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { catKeys, SavedNotice } from './common';

const STAGES = ['PreVisit', 'CheckIn', 'BeforeSession', 'AfterSession'] as const;
const RESTRICTIONS = ['MinAge', 'MaxAge', 'Gender', 'Prerequisite', 'MinInterval', 'MaxPerDay'] as const;

const formsSchema = z.object({
  items: z.array(
    z
      .object({
        kind: z.enum(['Form', 'Consent']),
        templateCode: z.string().min(1, { error: 'common:validation.required' }),
        stage: z.enum(STAGES),
        isMandatory: z.boolean(),
        validityMonths: z.string(),
      })
      .superRefine((value, ctx) => {
        const months = value.validityMonths.trim();
        if (
          months &&
          (value.kind !== 'Consent' ||
            !Number.isInteger(Number(months)) ||
            Number(months) < 1 ||
            Number(months) > 60)
        ) {
          ctx.addIssue({ code: 'custom', path: ['validityMonths'], message: 'errors:validation.invalid' });
        }
      }),
  ),
});
type FormsForm = z.input<typeof formsSchema>;

const restrictionsSchema = z.object({
  items: z.array(
    z
      .object({
        kind: z.enum(RESTRICTIONS),
        value: z.string(),
        relatedServiceId: z.string(),
        gender: z.string(),
      })
      .superRefine((value, ctx) => {
        if (value.kind === 'Gender' ? !value.gender : value.kind !== 'Prerequisite' && !value.value.trim()) {
          ctx.addIssue({
            code: 'custom',
            path: [value.kind === 'Gender' ? 'gender' : 'value'],
            message: 'common:validation.required',
          });
        }
        if (value.kind === 'Prerequisite' && !value.relatedServiceId) {
          ctx.addIssue({ code: 'custom', path: ['relatedServiceId'], message: 'common:validation.required' });
        }
      }),
  ),
});
type RestrictionsForm = z.input<typeof restrictionsSchema>;

function FormsSection({
  service,
  items,
  options,
}: {
  service: ServiceView;
  items: readonly RequiredFormDto[];
  options: readonly FormTemplateOptionView[];
}) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<FormsForm>(
    () => ({
      items: items.map((item) => ({
        kind: item.kind,
        templateCode: item.templateCode,
        stage: item.stage,
        isMandatory: item.isMandatory,
        validityMonths:
          item.validityMonths === null || item.validityMonths === undefined
            ? ''
            : String(item.validityMonths),
      })),
    }),
    [items],
  );
  const { control, handleSubmit, formState, setError } = useForm<FormsForm>({
    resolver: zodResolver(formsSchema),
    values,
  });
  const rows = useFieldArray({ control, name: 'items' });
  const watched = useWatch({ control, name: 'items' });

  const save = useMutation({
    mutationFn: (form: FormsForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/required-forms', {
          params: { path: { id: service.id } },
          body: {
            items: form.items.map((item) => ({
              kind: item.kind,
              templateCode: item.templateCode,
              stage: item.stage,
              isMandatory: item.isMandatory,
              validityMonths:
                item.kind === 'Consent' && item.validityMonths.trim() ? Number(item.validityMonths) : null,
            })),
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (data) => {
      setSaved(true);
      queryClient.setQueryData(catKeys.requirement(session?.tenantId, service.id, 'forms'), data);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: [],
        codeFields: { 'PHX-CAT-0402': 'items.0.templateCode' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">{t('cat:forms.title')}</Typography>
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() =>
                rows.append({
                  kind: 'Consent',
                  templateCode: '',
                  stage: 'BeforeSession',
                  isMandatory: true,
                  validityMonths: '',
                })
              }
              data-testid="form-add"
            >
              {t('cat:forms.add')}
            </Button>
          </Stack>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          {options.some((option) => option.isSample) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <SampleBadge />
              <Typography variant="caption" color="text.secondary">
                {t('cat:forms.sample_notice')}
              </Typography>
            </Stack>
          )}
          {rows.fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('cat:forms.none')}
            </Typography>
          )}
          {rows.fields.map((field, index) => {
            const kind = watched?.[index]?.kind ?? field.kind;
            return (
              <Stack
                key={field.id}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems="flex-start"
                data-testid={`form-${index}`}
              >
                <SelectRHF
                  control={control}
                  name={`items.${index}.kind`}
                  labelKey="cat:forms.kind"
                  disabled={!write.canWrite}
                  options={(['Form', 'Consent'] as const).map((value) => ({
                    value,
                    labelKey: `cat:forms.kinds.${value}`,
                  }))}
                />
                <Box sx={{ flex: 2, width: '100%' }}>
                  <SelectRHF
                    control={control}
                    name={`items.${index}.templateCode`}
                    labelKey="cat:forms.template"
                    required
                    fullWidth
                    disabled={!write.canWrite}
                    options={options
                      .filter((option) => option.kind === kind)
                      .map((option) => ({
                        value: option.code,
                        label: option.isSample ? `${option.name} (${t('common:sample.badge')})` : option.name,
                      }))}
                  />
                </Box>
                <SelectRHF
                  control={control}
                  name={`items.${index}.stage`}
                  labelKey="cat:forms.stage"
                  disabled={!write.canWrite}
                  options={STAGES.map((value) => ({ value, labelKey: `cat:forms.stages.${value}` }))}
                />
                {kind === 'Consent' && (
                  <TextFieldRHF
                    control={control}
                    name={`items.${index}.validityMonths`}
                    labelKey="cat:forms.validity"
                    helperKey="cat:forms.validity_help"
                    type="number"
                    disabled={!write.canWrite}
                    slotProps={{ htmlInput: { min: 1, max: 60, 'data-testid': `form-validity-${index}` } }}
                  />
                )}
                <CheckboxRHF
                  control={control}
                  name={`items.${index}.isMandatory`}
                  labelKey="cat:forms.mandatory"
                  disabled={!write.canWrite}
                />
                <IconButton
                  aria-label={t('cat:forms.remove')}
                  disabled={!write.canWrite}
                  onClick={() => rows.remove(index)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="forms-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

function RestrictionValue({
  control,
  index,
  kind,
  services,
  disabled,
}: {
  control: Control<RestrictionsForm>;
  index: number;
  kind: (typeof RESTRICTIONS)[number];
  services: readonly ServiceView[];
  disabled: boolean;
}) {
  if (kind === 'Gender') {
    return (
      <SelectRHF
        control={control}
        name={`items.${index}.gender`}
        labelKey="cat:restrictions.gender"
        required
        disabled={disabled}
        options={(['Female', 'Male'] as const).map((value) => ({
          value,
          labelKey: `cat:restrictions.genders.${value}`,
        }))}
      />
    );
  }

  return (
    <>
      {(kind === 'Prerequisite' || kind === 'MinInterval') && (
        <Box sx={{ flex: 2, width: '100%' }}>
          <SelectRHF
            control={control}
            name={`items.${index}.relatedServiceId`}
            labelKey="cat:restrictions.related_service"
            helperKey={kind === 'MinInterval' ? 'cat:restrictions.related_same_help' : undefined}
            required={kind === 'Prerequisite'}
            fullWidth
            disabled={disabled}
            options={[
              ...(kind === 'MinInterval' ? [{ value: '', labelKey: 'cat:restrictions.same_service' }] : []),
              ...services.map((service) => ({
                value: service.id,
                label: `${service.code} — ${service.name}`,
              })),
            ]}
          />
        </Box>
      )}
      <TextFieldRHF
        control={control}
        name={`items.${index}.value`}
        labelKey={`cat:restrictions.value_label.${kind}`}
        type="number"
        disabled={disabled}
        slotProps={{ htmlInput: { min: 0, 'data-testid': `restriction-value-${index}` } }}
      />
    </>
  );
}

function RestrictionsSection({
  service,
  items,
  services,
}: {
  service: ServiceView;
  items: readonly RestrictionDto[];
  services: readonly ServiceView[];
}) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<RestrictionsForm>(
    () => ({
      items: items.map((item) => ({
        kind: item.kind,
        value: item.value === null || item.value === undefined ? '' : String(item.value),
        relatedServiceId: item.relatedServiceId ?? '',
        gender: item.gender ?? '',
      })),
    }),
    [items],
  );
  const { control, handleSubmit, formState, setError } = useForm<RestrictionsForm>({
    resolver: zodResolver(restrictionsSchema),
    values,
  });
  const rows = useFieldArray({ control, name: 'items' });
  const watched = useWatch({ control, name: 'items' });
  const others = services.filter((candidate) => candidate.id !== service.id);

  const save = useMutation({
    mutationFn: (form: RestrictionsForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/restrictions', {
          params: { path: { id: service.id } },
          body: {
            items: form.items.map((item) => ({
              kind: item.kind,
              value: item.kind === 'Gender' || !item.value.trim() ? null : Number(item.value),
              relatedServiceId:
                (item.kind === 'Prerequisite' || item.kind === 'MinInterval') && item.relatedServiceId
                  ? item.relatedServiceId
                  : null,
              gender: item.kind === 'Gender' ? (item.gender as 'Female' | 'Male') : null,
            })),
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (data) => {
      setSaved(true);
      queryClient.setQueryData(catKeys.requirement(session?.tenantId, service.id, 'restrictions'), data);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: [],
        codeFields: { 'PHX-CAT-0403': 'items.0.relatedServiceId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">{t('cat:restrictions.title')}</Typography>
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() => rows.append({ kind: 'MinAge', value: '', relatedServiceId: '', gender: '' })}
              data-testid="restriction-add"
            >
              {t('cat:restrictions.add')}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('cat:restrictions.help')}
          </Typography>
          <SavedNotice show={saved && !formState.isDirty} />
          {rows.fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('cat:restrictions.none')}
            </Typography>
          )}
          {rows.fields.map((field, index) => (
            <Stack
              key={field.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems="flex-start"
              data-testid={`restriction-${index}`}
            >
              <SelectRHF
                control={control}
                name={`items.${index}.kind`}
                labelKey="cat:restrictions.kind"
                disabled={!write.canWrite}
                options={RESTRICTIONS.map((value) => ({
                  value,
                  labelKey: `cat:restrictions.kinds.${value}`,
                }))}
              />
              <RestrictionValue
                control={control}
                index={index}
                kind={watched?.[index]?.kind ?? field.kind}
                services={others}
                disabled={!write.canWrite}
              />
              <IconButton
                aria-label={t('cat:restrictions.remove')}
                disabled={!write.canWrite}
                onClick={() => rows.remove(index)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="restrictions-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

/** تب فرم‌ها و محدودیت‌ها. */
export function FormsTab({ service }: { service: ServiceView }) {
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('cat.service.manage');
  const forms = useQuery({
    queryKey: catKeys.requirement(session?.tenantId, service.id, 'forms'),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services/{id}/required-forms', {
          params: { path: { id: service.id } },
        }),
      ),
  });
  const restrictions = useQuery({
    queryKey: catKeys.requirement(session?.tenantId, service.id, 'restrictions'),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services/{id}/restrictions', { params: { path: { id: service.id } } }),
      ),
  });
  const options = useQuery({
    queryKey: catKeys.formOptions(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/form-template-options')),
    enabled: write.canWrite,
  });
  const services = useQuery({
    queryKey: catKeys.services(session?.tenantId, { picker: true }),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { limit: 200 } } })),
  });

  if (forms.isPending || restrictions.isPending || services.isPending) {
    return <LoadingState rows={3} />;
  }
  const error = forms.error ?? restrictions.error ?? services.error;
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          void forms.refetch();
          void restrictions.refetch();
          void services.refetch();
        }}
      />
    );
  }

  if (!forms.data || !restrictions.data || !services.data) {
    return <LoadingState rows={3} />;
  }

  return (
    <Stack spacing={2}>
      <FormsSection service={service} items={forms.data.items} options={options.data?.items ?? []} />
      <RestrictionsSection service={service} items={restrictions.data.items} services={services.data.items} />
    </Stack>
  );
}
